/**
 * Centralized React Query key factory.
 *
 * Mutations invalidate by key — keeping the key shapes in one place means
 * the dashboard hook and a mutation hook can't drift apart by accident.
 * Use only these factories from hooks; do not assemble query keys ad hoc.
 *
 * Pattern: each resource group has a top-level `all` array (for blanket
 * invalidation) plus more-specific tuple factories for read paths.
 */
export const queryKeys = {
  clinics: {
    /** Match every clinic-list query. */
    all: ["clinics"] as const,
    /** The `GET /v1/clinics` list. */
    list: () => ["clinics", "list"] as const,
  },
  dashboard: {
    /** Match every dashboard query (for blanket invalidation). */
    all: ["dashboard"] as const,
    /** The dashboard for one clinic-year (optionally pinned to a meeting). */
    one: (clinicId: number, year: number, meetingId?: number) =>
      ["dashboard", clinicId, year, meetingId ?? null] as const,
    /** Match every dashboard query for one clinic, all years. */
    byClinic: (clinicId: number) => ["dashboard", clinicId] as const,
  },
  scoringRuns: {
    all: ["scoring-runs"] as const,
    list: (year?: number) => ["scoring-runs", year ?? null] as const,
  },
  clinicMeetings: {
    /** Match every clinic-meetings query (any clinic). */
    all: ["clinic-meetings"] as const,
    /** Match every clinic-meetings query for one clinic. */
    byClinic: (clinicId: number) => ["clinic-meetings", clinicId] as const,
    /** Specific list query for one clinic + filter set. */
    list: (
      clinicId: number,
      options?: { year?: number; includeCancelled?: boolean; limit?: number },
    ) => ["clinic-meetings", clinicId, options ?? null] as const,
  },
  // ------ V1.2.0 (backend v0.4.0) ------
  adminClinics: {
    /** Match every admin clinics query. */
    all: ["admin-clinics"] as const,
    /** Filtered list (mips_active true/false/undefined). */
    list: (mipsActive?: boolean) =>
      ["admin-clinics", mipsActive ?? null] as const,
  },
  ia: {
    /** Match every IA read for any clinic. */
    all: ["ia"] as const,
    /** IA read for one clinic + year. */
    byClinic: (clinicId: number, year?: number) =>
      ["ia", clinicId, year ?? null] as const,
  },
  pi: {
    /** Match every PI read for any clinic. */
    all: ["pi"] as const,
    /** PI read for one clinic + year. */
    byClinic: (clinicId: number, year?: number) =>
      ["pi", clinicId, year ?? null] as const,
  },
  // ------ V1.2.1 (backend v0.4.1) ------
  specialtySets: {
    /** Match every specialty-set catalog query. */
    all: ["specialty-sets"] as const,
    /** Catalog for a specific performance year. */
    list: (year?: number) => ["specialty-sets", year ?? null] as const,
  },
} as const;
